import GitError from "@ext/git/core/GitCommands/errors/GitError";
import { LibGit2Error } from "@ext/git/core/GitCommands/errors/LibGit2Error";
import GitErrorCode from "@ext/git/core/GitCommands/errors/model/GitErrorCode";
import t from "@ext/localization/locale/translate";

const getGitErrorText = (error: unknown, fallback: string): string => {
	if (isHealthcheckFailure(error)) return t("git.error.broken.healthcheck.title");
	if (error instanceof LibGit2Error) return error.reason || fallback;
	if (error instanceof GitError) return htmlToText(error.message);
	return fallback;
};

// libgit2 errors append `Args: {…}` and file-system errors `args: {…}`: call arguments, paths and the author.
const ARGS_DUMP = /;?\s*\n\s*args:[\s\S]*$/i;

/** Also for an error that arrived as plain data, its class lost on the way from native code or a worker. */
export const getReadableErrorText = (error: unknown, fallback: string): string => {
	if (error instanceof LibGit2Error || error instanceof GitError) return getGitErrorText(error, fallback);
	const data = error as { reason?: string; message?: string } | null;
	const text = data?.reason || data?.message?.replace(ARGS_DUMP, "");
	return (text && htmlToText(text)) || fallback;
};

export const getGitErrorCode = (error: unknown): GitErrorCode | undefined => {
	const data = error as { code?: unknown; props?: { errorCode?: unknown } } | null;
	const code = data?.props?.errorCode ?? data?.code;
	return typeof code === "string" && Object.hasOwn(GitErrorCode, code) ? (code as GitErrorCode) : undefined;
};

const isHealthcheckFailure = (error: unknown): boolean =>
	(error instanceof LibGit2Error && error.code === GitErrorCode.HealthcheckFailed) ||
	(error instanceof GitError && error.props.errorCode === GitErrorCode.HealthcheckFailed);

// Error messages are written for the HTML error modal: links, <code>, <br>.
const htmlToText = (html: string): string =>
	html
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();

export default getGitErrorText;
